/**
 * Credentialed same-origin calls to session Netlify Functions.
 * Relative URLs only — never hop to a different origin for cookies.
 */

const VALIDATE_URL = '/.netlify/functions/validateAccess';
const SESSION_URL = '/.netlify/functions/session';
const LOGOUT_URL = '/.netlify/functions/logout';

export type ValidateAccessResult = { ok: boolean; status: number };
export type SessionResult = { hasAccess: boolean; expiresAt?: number };

async function parseJson(response: Response): Promise<Record<string, unknown>> {
  return (await response.json().catch(() => ({}))) as Record<string, unknown>;
}

export async function validateAccess(code: string): Promise<ValidateAccessResult> {
  const response = await fetch(VALIDATE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ code }),
  });
  const data = await parseJson(response);
  return { ok: response.ok && data.ok === true, status: response.status };
}

export async function getSession(): Promise<SessionResult> {
  const response = await fetch(SESSION_URL, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    return { hasAccess: false };
  }
  const data = await parseJson(response);
  const hasAccess = data.hasAccess === true;
  const expiresAt =
    typeof data.expiresAt === 'number' ? data.expiresAt : undefined;
  return { hasAccess, expiresAt };
}

export async function logout(): Promise<void> {
  await fetch(LOGOUT_URL, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  }).catch(() => {
    /* best-effort */
  });
}

const accessClient = {
  validateAccess,
  getSession,
  logout,
};

export default accessClient;
